
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { signUp, SignUpData } from '@/services/auth.service';
import { useAuth } from '@/contexts/AuthContext';
import { Separator } from '@/components/ui/separator';
import { SSOButtonGroup } from '@/components/auth/SSOButtonGroup';
import { CollapsibleEmailAuth } from '@/components/auth/CollapsibleEmailAuth';
import { UserRoleSelection } from '@/components/auth/UserRoleSelection';
import { TermsAndConditions } from '@/components/auth/TermsAndConditions';
import { LoadingSpinner } from '@/components/auth/LoadingSpinner';

import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Mail } from 'lucide-react';
import { UserRole } from '@/types/user-roles';
import { supabase } from '@/integrations/supabase/client';

import { signInWithPhone, verifyOtp } from '@/services/auth.service';
import { PasswordStrengthResult } from '@/utils/passwordStrength';
import { Link } from 'react-router-dom';
import { cleanupAuthState } from '@/utils/authCleanup';
import { trackEvent } from '@/analytics/ga';
import { BrandLogo } from '@/components/ui/brand-logo';
const SignUp = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('regular');
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [showEmailSent, setShowEmailSent] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrengthResult | null>(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showTermsValidationError, setShowTermsValidationError] = useState(false);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    
    // Check if terms are agreed
    if (!agreedToTerms) {
      setShowTermsValidationError(true);
      return;
    }

    // Check password strength
    if (!passwordStrength || !passwordStrength.isValid) {
      toast.error('Please create a stronger password');
      return;
    }

    setIsSubmitting(true);
    setSignupError(null);

    try {
      const userData: SignUpData = {
        firstName,
        lastName,
        role,
        organizationName,
        selectedProviderId: role === 'peo' ? selectedProviderId : undefined
      };

      const { error } = await signUp(email, password, userData);

      if (error) throw error;
      
      // Track successful signup
      trackEvent('sign_up', { method: 'email' });
      
      // Redirect to verification page with email
      toast.success('Account created! Please check your email to verify your account.');
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (error: any) {
      console.error('Signup error:', error);
      setSignupError(error.message || 'An error occurred during signup');
      toast.error(error.message || 'An error occurred during signup');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialSignup = async (provider: 'google' | 'linkedin_oidc' | 'azure' | 'apple') => {
    setHasAttemptedSubmit(true);
    
    // Validate terms agreement and role selection before allowing SSO
    if (!agreedToTerms) {
      setShowTermsValidationError(true);
      return;
    }

    if (!role) {
      toast.error('Please select your user type before continuing');
      return;
    }

    try {
      // Track signup intent
      trackEvent('sign_up_intent', { method: provider });
      
      // Store user preferences in localStorage temporarily for the callback
      localStorage.setItem('pendingSignupData', JSON.stringify({
        role: role,
        terms_agreed: true,
        terms_agreed_at: new Date().toISOString(),
        organizationName: organizationName,
        provider: provider, // Store which provider was used
        timestamp: Date.now()
      }));

      // Log OAuth attempt for debugging
      console.log(`Initiating ${provider} OAuth with redirect to:`, `${window.location.origin}/dashboard`);
      console.log('Current URL:', window.location.href);
      console.log('OAuth Configuration Check:', {
        provider,
        redirectTo: `${window.location.origin}/dashboard`,
        currentOrigin: window.location.origin,
        storedData: JSON.parse(localStorage.getItem('pendingSignupData') || '{}')
      });
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: { 
            prompt: 'select_account',
            access_type: 'offline' // Request refresh token
          },
        }
      });
      
      if (error) {
        console.error(`${provider} OAuth error:`, {
          message: error.message,
          status: error.status,
          details: error,
          url: window.location.href,
          origin: window.location.origin
        });
        throw error;
      }
      
      console.log(`${provider} OAuth initiated successfully`);
    } catch (error: any) {
      console.error(`Social signup error for ${provider}:`, {
        message: error.message,
        status: error.status,
        name: error.name,
        stack: error.stack
      });
      
      // Clear stored data on error
      localStorage.removeItem('pendingSignupData');
      
      // Provide more specific error messages
      let errorMessage = error.message || `An error occurred during ${provider} signup`;
      
      if (error.message?.includes('404') || error.status === 404) {
        errorMessage = `Authentication failed - ${provider} OAuth is not properly configured. Please verify the configuration in Supabase Dashboard.`;
        console.error('OAuth 404 Error - Configuration Issue:', {
          provider,
          redirectUrl: `${window.location.origin}/dashboard`,
          currentUrl: window.location.href,
          currentOrigin: window.location.origin,
          suggestedActions: [
            'Check Supabase Auth Provider configuration',
            'Verify OAuth provider settings (LinkedIn/Microsoft)',
            'Ensure redirect URLs match'
          ]
        });
      } else if (error.message?.includes('redirect_uri_mismatch') || /origin_mismatch|invalid_client/i.test(error.message || '')) {
        errorMessage = `URL configuration error - please verify the OAuth configuration in Supabase Dashboard and ensure ${window.location.origin} is added to the authorized origins.`;
        console.error('OAuth Origin/Redirect Mismatch:', {
          provider,
          currentOrigin: window.location.origin,
          redirectUrl: `${window.location.origin}/dashboard`,
          error: error
        });
      }
      
      setSignupError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handlePhoneSignup = async (phone: string) => {
    setHasAttemptedSubmit(true);
    
    // Validate terms agreement and role selection before allowing phone signup
    if (!agreedToTerms) {
      setShowTermsValidationError(true);
      return;
    }

    if (!role) {
      toast.error('Please select your user type before continuing');
      return;
    }

    try {
      setSignupError(null);
      
      // Store signup data temporarily for the OTP verification
      const signupData = {
        firstName,
        lastName,
        role,
        organizationName,
        agreedToTerms,
        terms_agreed_at: new Date().toISOString()
      };
      localStorage.setItem('phoneSignupData', JSON.stringify(signupData));
      
      const { error } = await signInWithPhone(phone);
      if (error) throw error;
      
      setPhoneNumber(phone);
      setShowOTP(true);
      toast.success('Verification code sent to your phone');
    } catch (error: any) {
      console.error('Phone signup error:', error);
      // Clear stored data on error
      localStorage.removeItem('phoneSignupData');
      setSignupError(error.message || 'Failed to send verification code');
      toast.error(error.message || 'Failed to send verification code');
    }
  };

  const handleOTPVerify = async (token: string) => {
    try {
      setSignupError(null);
      
      // Retrieve stored signup data
      const storedData = localStorage.getItem('phoneSignupData');
      let userData = undefined;
      
      if (storedData) {
        const signupData = JSON.parse(storedData);
        userData = {
          firstName: signupData.firstName || firstName,
          lastName: signupData.lastName || lastName,
          role: signupData.role || role,
          organizationName: signupData.organizationName || organizationName
        };
      }
      
      const { error } = await verifyOtp(phoneNumber, token, userData);
      if (error) throw error;
      
      // Clear stored data after successful verification
      localStorage.removeItem('phoneSignupData');
      
      // Track successful signup
      trackEvent('sign_up', { method: 'phone' });
      
      toast.success('Successfully signed up and logged in');
      setShowOTP(false);
    } catch (error: any) {
      console.error('OTP verification error:', error);
      // Clear stored data on error to prevent data leakage
      localStorage.removeItem('phoneSignupData');
      setSignupError(error.message || 'Invalid verification code');
      toast.error(error.message || 'Invalid verification code');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (showEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex justify-center">
              <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
            </div>
            <div className="mt-6 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900">
                Check your email
              </h2>
              <p className="mt-2 text-gray-600">
                We've sent a verification link to <strong>{email}</strong>
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Click the link in the email to verify your account and access all features.
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <Button
              onClick={() => navigate('/login')}
              className="w-full py-6"
            >
              Continue to Login
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowEmailSent(false)}
              className="w-full py-6"
            >
              Back to Sign Up
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleGoBack}
          className="absolute top-4 left-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        
        <div>
          <div className="flex justify-center">
            <BrandLogo imgClassName="h-12 w-auto" showAsLink={false} />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Let's get you started!
          </h2>
          <p className="mt-2 text-center text-gray-600">
            Takes just a minute. No commitment required.
          </p>
        </div>

        {signupError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4 mr-2" />
            <AlertDescription>{signupError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <SSOButtonGroup
            onGoogleClick={() => handleSocialSignup('google')}
            onLinkedInClick={() => handleSocialSignup('linkedin_oidc')}
            onMicrosoftClick={() => handleSocialSignup('azure')}
            onPhoneSubmit={handlePhoneSignup}
            onOTPSubmit={handleOTPVerify}
            disabled={isSubmitting}
            showOTP={showOTP}
            phoneNumber={phoneNumber}
            hideMicrosoft={true}
            hidePhone={role === 'peo'}
          />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gradient-to-br from-white to-green-50 px-2 text-muted-foreground"></span>
            </div>
          </div>

          <CollapsibleEmailAuth
            firstName={firstName}
            lastName={lastName}
            email={email}
            password={password}
            organizationName={organizationName}
            isSubmitting={isSubmitting}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onOrganizationNameChange={setOrganizationName}
            onSubmit={handleSubmit}
            passwordStrength={passwordStrength}
            onPasswordStrengthChange={setPasswordStrength}
          />

          <UserRoleSelection 
            role={role}
            onRoleChange={setRole}
            selectedProviderId={selectedProviderId}
            onProviderChange={setSelectedProviderId}
          />

          <TermsAndConditions 
            agreedToTerms={agreedToTerms}
            onAgreedToTermsChange={(checked) => {
              setAgreedToTerms(checked);
              if (checked) {
                setShowTermsValidationError(false);
              }
            }}
            hasAttemptedSubmit={hasAttemptedSubmit}
            showValidationError={showTermsValidationError}
          />

          <div className="relative">
            <Button
              type="submit"
              className={`
                w-full transition-all duration-300 group
                ${!agreedToTerms || (passwordStrength && passwordStrength.level !== 'Strong') ? 
                  'opacity-70 cursor-not-allowed' : 
                  'hover:shadow-elevation-2'
                }
              `}
              disabled={isSubmitting || !agreedToTerms || (passwordStrength && passwordStrength.level !== 'Strong')}
              onClick={handleSubmit}
              title={
                !agreedToTerms ? 
                  "Please accept the terms and conditions to continue" :
                  (passwordStrength && passwordStrength.level !== 'Strong') ?
                    "Please create a stronger password" :
                    undefined
              }
            >
              {isSubmitting ? 'Creating account...' : 'Get Started'}
            </Button>
            
            {(!agreedToTerms || (passwordStrength && passwordStrength.level !== 'Strong')) && (
              <div className="mt-3 p-2 bg-muted/50 rounded-md border border-muted-foreground/20">
                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-2">
                  <AlertCircle className="h-3 w-3" />
                  {!agreedToTerms ? 
                    "Accept terms and conditions to continue" :
                    "Create a stronger password to continue"
                  }
                </p>
              </div>
            )}
          </div>
          
          <p className="text-center text-sm text-gray-600 mt-4">
            Already signed up?{' '}
            <Link
              to="/login"
              className="text-primary hover:text-primary-dark font-medium"
            >
              Log back in here
            </Link>
          </p>
        </div>

        
      </div>
    </div>
  );
};

export default SignUp;
